import { useEffect, useRef, useState } from "react";
import WavingLinesOverlay from "./WavingLinesOverlay";

function StarCanvas({ starCount = 160, bandCount = 5 }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let raf = 0;
    let t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    window.addEventListener("resize", resize);

    const stars = Array.from({ length: starCount }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.1 + 0.2,
      base: Math.random() * 0.4 + 0.06,
      phase: Math.random() * Math.PI * 2,
      spd: Math.random() * 0.018 + 0.005,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const width = canvas.width;
      const height = canvas.height;

      stars.forEach((star) => {
        const alpha = star.base * (0.5 + 0.5 * Math.sin(star.phase + t * star.spd * 18));
        ctx.beginPath();
        ctx.arc(star.x * width, star.y * height, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(198,226,245,${alpha})`;
        ctx.fill();
      });

      for (let layer = 0; layer < bandCount; layer += 1) {
        ctx.beginPath();
        const yBase = height * (0.3 + layer * 0.1);
        const amp = 25 + layer * 20;
        const freq = 0.0012 + layer * 0.0006;
        const speed = 0.25 + layer * 0.18;
        const hue = 188 + layer * 8;

        for (let x = 0; x <= width; x += 3) {
          const y =
            yBase +
            Math.sin(x * freq + t * speed) * amp +
            Math.sin(x * freq * 1.9 + t * speed * 0.5) * amp * 0.35;

          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.strokeStyle = `hsla(${hue},78%,64%,${0.025 + layer * 0.013})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      t += 0.01;
      raf = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [starCount, bandCount]);

  return (
    <canvas
      ref={ref}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 1,
        pointerEvents: "none",
      }}
    />
  );
}

export default function HomeAuroraBackground({
  showStars = false,
  showWavingLines = false,
  className = "pointer-events-none absolute inset-0 z-0 overflow-hidden",
}) {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let resizeRaf = 0;

    const onResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        setViewportWidth(window.innerWidth);
      });
    };

    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(resizeRaf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const isSmallMobile = viewportWidth < 480;
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1120;
  const starCount = isSmallMobile ? 72 : isMobile ? 96 : isTablet ? 120 : 160;
  const bandCount = isMobile ? 3 : 5;

  return (
    <div className={className}>
      <style>{`
        @keyframes homeAuroraPulse {
          0%, 100% { opacity: .45; transform: scale(1); }
          50% { opacity: .88; transform: scale(1.04); }
        }
      `}</style>

      {showStars ? <StarCanvas starCount={starCount} bandCount={bandCount} /> : null}
      {showWavingLines ? <WavingLinesOverlay /> : null}

      <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <div
          style={{
            position: "absolute",
            top: "-18%",
            left: "45%",
            transform: "translateX(-50%)",
            width: "clamp(420px,88vw,1000px)",
            height: "clamp(300px,60vw,680px)",
            borderRadius: "50%",
            background:
              "radial-gradient(ellipse,rgba(62,167,185,0.45) 0%,rgba(41,92,172,0.28) 45%,transparent 72%)",
            filter: "blur(70px)",
            animation: "homeAuroraPulse 8s ease-in-out infinite",
          }}
        />

        <div
          style={{
            position: "absolute",
            bottom: "-12%",
            left: "-8%",
            width: "clamp(280px,52vw,600px)",
            height: "clamp(280px,52vw,600px)",
            borderRadius: "50%",
            background: "radial-gradient(ellipse,rgba(95,182,67,0.38) 0%,transparent 65%)",
            filter: "blur(90px)",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: "10%",
            right: "-5%",
            width: "clamp(260px,48vw,520px)",
            height: "clamp(260px,48vw,520px)",
            borderRadius: "50%",
            background: "radial-gradient(ellipse,rgba(62,167,185,0.36) 0%,transparent 65%)",
            filter: "blur(90px)",
            animation: "homeAuroraPulse 11s ease-in-out infinite reverse",
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(rgba(255,255,255,0.055) 1px,transparent 1px)",
            backgroundSize: isSmallMobile ? "24px 24px" : "34px 34px",
            maskImage: "radial-gradient(ellipse 100% 100% at 50% 30%,black 40%,transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 100% 100% at 50% 30%,black 40%,transparent 100%)",
          }}
        />
      </div>
    </div>
  );
}
